import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { alpha, darken } from "@mui/material/styles";
import { ExerciseCard } from "./ExerciseCard";
import { isPersonalizedTrainingSlot } from "../utils/personalizedTrainingSlot";
import { normalizeAppLang } from "../utils/appLanguages";
import {
  localizedExerciseName,
  localizedSubTypeName,
  localizedTypeName,
} from "../utils/localizedCatalogFields";
import { ip_back } from "../config";
import axios from "axios";
import { useBrand } from "../context/BrandContext";

const PersonalizeExerciseDialog = ({
  open,
  onClose,
  container,
  exercise,
  groupPlayers,
  selectedSession,
  selectedgrp,
  exerciseTypes,
  currentlng,
  t,
  onExerciseSelected,
  preSelectedPlayerId,
}) => {
  const { brand } = useBrand();
  const primary = brand?.color_primary || "#0877BC";
  const primaryDark = brand?.color_primary_dark || "#065a8f";

  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedSubTypeId, setSelectedSubTypeId] = useState("");
  const [subtypes, setSubtypes] = useState([]);
  const [loadingSubtypes, setLoadingSubtypes] = useState(false);
  const [recommendedExercises, setRecommendedExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [searched, setSearched] = useState(false);
  const [playerLevel, setPlayerLevel] = useState("");

  useEffect(() => {
    if (open && exercise) {
      const personalized = isPersonalizedTrainingSlot(exercise);
      const typeId = personalized ? "" : exercise.type_ex?.type_id || "";
      const subTypeId = personalized ? "" : exercise.sub_type_ex?.sub_type_id || "";
      setSelectedTypeId(typeId);
      setSelectedSubTypeId(subTypeId);
      setSelectedPlayerId(preSelectedPlayerId || "");
      setRecommendedExercises([]);
      setSelectedRecommendation(null);
      setSearched(false);
      setPlayerLevel("");
      if (typeId) {
        fetchSubtypes(typeId);
      } else {
        setSubtypes([]);
      }
    }
  }, [open, exercise, preSelectedPlayerId]);

  const fetchSubtypes = async (typeId) => {
    setLoadingSubtypes(true);
    try {
      const response = await axios.get(
        `${ip_back}api/types/getSubtype?type_id=${typeId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (response.data.status === "success") {
        setSubtypes(response.data.data || []);
      }
    } catch {
      setSubtypes([]);
    } finally {
      setLoadingSubtypes(false);
    }
  };

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    setSelectedTypeId(typeId);
    setSelectedSubTypeId("");
    setRecommendedExercises([]);
    setSelectedRecommendation(null);
    setSearched(false);
    if (typeId) fetchSubtypes(typeId);
    else setSubtypes([]);
  };

  const handleSearch = async () => {
    if (!selectedPlayerId) return;
    setLoading(true);
    setRecommendedExercises([]);
    setSelectedRecommendation(null);
    setSearched(true);
    try {
      const body = {
        player_id: selectedPlayerId,
        ts_id: selectedSession?.ts_id,
        gr_id: selectedgrp?.groupDetails?.gr_id,
        language: currentlng?.substring(0, 2) || "en",
        session_type: "main",
      };
      if (selectedTypeId) body.type_id = selectedTypeId;
      if (selectedSubTypeId) body.sub_type_id = selectedSubTypeId;

      const response = await axios.post(
        `${ip_back}api/exercises/recommend-for-player`,
        body,
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (response.data.status === "success" && response.data.data?.exercises) {
        setRecommendedExercises(response.data.data.exercises);
        setPlayerLevel(response.data.data.player_level || "");
      }
    } catch (err) {
      console.error("Personalize search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRecommendation = (ex) => {
    setSelectedRecommendation(ex.ex_id === selectedRecommendation?.ex_id ? null : ex);
  };

  const handleApply = () => {
    if (selectedRecommendation && onExerciseSelected) {
      onExerciseSelected(selectedRecommendation, selectedPlayerId);
    }
  };

  const lang = normalizeAppLang(currentlng);

  const getPlayerName = (gp) => {
    if (!gp) return "";
    return (
      gp.user_alias ||
      `${gp.user_first_name || ""} ${gp.user_last_name || ""}`.trim() ||
      gp.player_id ||
      (t("player", { lng: lang }) || "Player")
    );
  };

  const selectedPlayer = groupPlayers?.find((p) => p.player_id === selectedPlayerId);
  const playerDisplay = selectedPlayer ? getPlayerName(selectedPlayer) : "";

  const exerciseName = localizedExerciseName(exercise, lang);
  const currentTypeName = localizedTypeName(exercise?.type_ex, lang);
  const currentSubTypeName = localizedSubTypeName(exercise?.sub_type_ex, lang);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      container={container || undefined}
      disableScrollLock={Boolean(container)}
      sx={container ? { zIndex: 5000 } : undefined}
      PaperProps={{
        sx: {
          maxHeight: "92vh",
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
          color: "#fff",
          pb: 2,
          pt: 2.5,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TuneIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2 }}>
              {t("personalizeExercise", { lng: lang }) || "Personalize Exercise"}
            </Typography>
            {exerciseName && (
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3, fontSize: "0.85rem" }}>
                {exerciseName}
              </Typography>
            )}
          </Box>
        </Box>
        {/* Current type/subtype badge */}
        {(currentTypeName || currentSubTypeName) && (
          <Box sx={{ display: "flex", gap: 0.8, mt: 1.5, flexWrap: "wrap" }}>
            {currentTypeName && (
              <Chip
                size="small"
                label={currentTypeName}
                sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 500, fontSize: "0.75rem" }}
              />
            )}
            {currentSubTypeName && (
              <Chip
                size="small"
                label={currentSubTypeName}
                sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 500, fontSize: "0.75rem" }}
              />
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Player Selection Section */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: brand?.color_background || "#f8fafc",
            borderBottom: `1px solid ${alpha(primary, 0.12)}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#37474f", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            <PersonIcon sx={{ fontSize: 20, color: primary }} />
            {t("selectPlayer", { lng: lang }) || "Select Player"}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>{t("player", { lng: lang }) || "Player"}</InputLabel>
            <Select
              value={selectedPlayerId}
              label={t("player", { lng: lang }) || "Player"}
              onChange={(e) => {
                setSelectedPlayerId(e.target.value);
                setRecommendedExercises([]);
                setSelectedRecommendation(null);
                setSearched(false);
              }}
              renderValue={(val) => {
                const gp = groupPlayers?.find((g) => g.player_id === val);
                return gp ? getPlayerName(gp) : val;
              }}
              sx={{
                backgroundColor: brand?.color_surface || "#fff",
                borderRadius: "10px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(primary, 0.22) },
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: "min(400px, 60vh)" } }, disableScrollLock: true }}
            >
              {groupPlayers?.map((gp) => {
                const playerId = gp.player_id;
                const name = getPlayerName(gp);
                const imgSrc = gp.user_image ? `${ip_back}${gp.user_image}` : null;
                return (
                  <MenuItem key={playerId} value={playerId}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar src={imgSrc} sx={{ width: 28, height: 28, fontSize: 14, bgcolor: primary }}>
                        {name.charAt(0).toUpperCase()}
                      </Avatar>
                      <span>{name}</span>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Type & Subtype Filters */}
        <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${alpha(primary, 0.12)}` }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#37474f", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
          >
            <TuneIcon sx={{ fontSize: 20, color: primary }} />
            {t("exerciseFilters", { lng: lang }) || "Exercise Filters"}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: 180 }}>
              <InputLabel>{t("exerciseType", { lng: lang }) || "Exercise Type"}</InputLabel>
              <Select
                value={selectedTypeId}
                label={t("exerciseType", { lng: lang }) || "Exercise Type"}
                onChange={handleTypeChange}
                sx={{ backgroundColor: brand?.color_surface || "#fff", borderRadius: "10px" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: "min(400px, 60vh)" } }, disableScrollLock: true }}
              >
                <MenuItem value="">
                  <em>{t("all", { lng: lang }) || "All"}</em>
                </MenuItem>
                {exerciseTypes?.map((type) => (
                  <MenuItem key={type.type_id} value={type.type_id}>
                    {localizedTypeName(type, lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: 180 }} disabled={!selectedTypeId || loadingSubtypes}>
              <InputLabel>{t("exerciseSubtype", { lng: lang }) || "Exercise Subtype"}</InputLabel>
              <Select
                value={selectedSubTypeId}
                label={t("exerciseSubtype", { lng: lang }) || "Exercise Subtype"}
                onChange={(e) => {
                  setSelectedSubTypeId(e.target.value);
                  setRecommendedExercises([]);
                  setSelectedRecommendation(null);
                  setSearched(false);
                }}
                sx={{ backgroundColor: brand?.color_surface || "#fff", borderRadius: "10px" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: "min(400px, 60vh)" } }, disableScrollLock: true }}
              >
                <MenuItem value="">
                  <em>{t("all", { lng: lang }) || "All"}</em>
                </MenuItem>
                {subtypes.map((st) => (
                  <MenuItem key={st.sub_type_id} value={st.sub_type_id}>
                    {localizedSubTypeName(st, lang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Search Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearch}
            disabled={!selectedPlayerId || loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            sx={{
              mt: 2,
              py: 1.3,
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: "0.95rem",
              textTransform: "none",
              background: selectedPlayerId
                ? `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`
                : "#b0bec5",
              boxShadow: selectedPlayerId ? `0 4px 14px ${alpha(primary, 0.35)}` : "none",
              "&:hover": {
                background: selectedPlayerId
                  ? `linear-gradient(135deg, ${primaryDark} 0%, ${darken(primaryDark, 0.12)} 100%)`
                  : "#b0bec5",
              },
            }}
          >
            {loading
              ? (t("searching", { lng: lang }) || "Searching...")
              : (t("searchRecommendations", { lng: lang }) || "Search")}
          </Button>
        </Box>

        {/* Results Section */}
        <Box sx={{ px: 3, py: 2.5, minHeight: 120 }}>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5, gap: 2 }}>
              <CircularProgress size={40} sx={{ color: primary }} />
              <Typography variant="body2" color="text.secondary">
                {t("searchingPersonalized", { lng: lang }) || "Finding personalized exercises..."}
              </Typography>
            </Box>
          ) : searched && recommendedExercises.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 5,
                gap: 1,
                color: "#78909c",
              }}
            >
              <SearchIcon sx={{ fontSize: 48, opacity: 0.4 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t("noPersonalizedResults", { lng: lang }) || "No matching exercises found"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("tryDifferentFilters", { lng: lang }) || "Try adjusting the type or subtype filters"}
              </Typography>
            </Box>
          ) : recommendedExercises.length > 0 ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#37474f", display: "flex", alignItems: "center", gap: 1 }}
                >
                  <StarIcon sx={{ fontSize: 20, color: "#f9a825" }} />
                  {t("recommendedForPlayer", { lng: lang }) || "Recommended for"}{" "}
                  <span style={{ color: primary }}>{playerDisplay}</span>
                </Typography>
                {playerLevel && (
                  <Chip
                    size="small"
                    label={playerLevel}
                    sx={{
                      backgroundColor:
                        playerLevel === "beginner"
                          ? "#e8f5e9"
                          : playerLevel === "advanced"
                            ? "#fce4ec"
                            : alpha(primary, 0.12),
                      color:
                        playerLevel === "beginner"
                          ? "#2e7d32"
                          : playerLevel === "advanced"
                            ? "#c62828"
                            : primaryDark,
                      fontWeight: 600,
                      textTransform: "capitalize",
                    }}
                  />
                )}
              </Box>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {recommendedExercises.map((ex) => (
                  <Box
                    key={ex.ex_id}
                    sx={{
                      position: "relative",
                      borderRadius: "12px",
                      border:
                        selectedRecommendation?.ex_id === ex.ex_id
                          ? `2px solid ${primary}`
                          : "2px solid transparent",
                      transition: "all 0.2s ease",
                      "&:hover": { boxShadow: `0 2px 12px ${alpha(primary, 0.14)}` },
                    }}
                  >
                    {selectedRecommendation?.ex_id === ex.ex_id && (
                      <CheckCircleIcon
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          fontSize: 26,
                          color: primary,
                          backgroundColor: brand?.color_surface || "#fff",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                    <ExerciseCard
                      exercise={{ ...ex, duration: ex.duration ?? 10 }}
                      isSelected={selectedRecommendation?.ex_id === ex.ex_id}
                      onSelect={() => handleSelectRecommendation(ex)}
                      lng={currentlng}
                      t={t}
                      selectMode="single"
                      showDurationInput={false}
                    />
                  </Box>
                ))}
              </div>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 5,
                gap: 1,
                color: "#b0bec5",
              }}
            >
              <TuneIcon sx={{ fontSize: 48, opacity: 0.4 }} />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 280 }}>
                {t("personalizeInstructions", { lng: lang }) || "Click on a player's name on any table to personalize the exercise for them."}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions
        sx={{
          borderTop: `1px solid ${alpha(primary, 0.12)}`,
          px: 3,
          py: 2,
          backgroundColor: brand?.color_background || "#fafbfc",
        }}
      >
        <Button
          onClick={onClose}
          sx={{ color: "#666", textTransform: "none", fontWeight: 500 }}
        >
          {t("cancel", { lng: lang }) || "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!selectedRecommendation}
          startIcon={<CheckCircleIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            px: 3,
            background: selectedRecommendation
              ? `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`
              : "#b0bec5",
            boxShadow: selectedRecommendation ? `0 4px 14px ${alpha(primary, 0.35)}` : "none",
            "&:hover": {
              background: selectedRecommendation
                ? `linear-gradient(135deg, ${primaryDark} 0%, ${darken(primaryDark, 0.12)} 100%)`
                : "#b0bec5",
            },
          }}
        >
          {t("applyPersonalization", { lng: lang }) || "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonalizeExerciseDialog;
